import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
  HttpClient
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, finalize, switchMap, filter, take, tap } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { EncryptionService } from './encryption.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionPlanService {
  private readonly subscriptionPlanSubject = new BehaviorSubject<{show: boolean, message?: string}>({show: false});
  public subscriptionPlan$ = this.subscriptionPlanSubject.asObservable();

  showSubscriptionPopup(message?: string) {
    this.subscriptionPlanSubject.next({show: true, message});
  }

  hideSubscriptionPopup() {
    this.subscriptionPlanSubject.next({show: false});
  }
}

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private requestQueue: HttpRequest<any>[] = [];
  private isProcessingSubscriptionError = false; // Flag to prevent multiple simultaneous calls

  constructor(
    private readonly loadingService: LoadingService,
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly encryptionService: EncryptionService,
    private readonly subscriptionPlanService: SubscriptionPlanService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token refresh for the refresh token request itself
    if (req.url.includes('refresh-token')) {
      return this.handleRefreshRequest(req, next);      
    }

    // Get the access token from localStorage

    // Clone the request and add the authorization header if token exists  
    const modifiedReq = this.addAuthHeader(req);

    // Check if this is a notification API call - don't show loading for these
    const isNotificationAPI = this.isNotificationAPI(req.url);

    // Show loading spinner with a custom message based on the request method
    if (!isNotificationAPI) {
      const loadingMessage = this.getLoadingMessage(req.method);
      this.loadingService.show(loadingMessage);
    }

    return next.handle(modifiedReq).pipe(
      tap((event: HttpEvent<any>) => {
        // Check for logo_url in successful responses
        if (event instanceof HttpResponse) {
          // Check if the response has logo_url in data
          this.handleLogoUrl(event.body);
        }
      }),
      catchError((error: HttpErrorResponse) =>
        this.handleError(error, req, next)
      ),
      // Add a tap operator to check successful responses for subscription errors
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {          
          this.handleSubscriptionErrorResponse(event.body);
        }
      }),
      finalize(() => {
        // Hide loading spinner when request completes (success or error)
        // Only hide if we showed it (i.e., not for notification APIs)
        if (!isNotificationAPI) {
          this.loadingService.hide();
        }
      })
    );
  }

  /** Handle refresh-token request explicitly */
  private handleRefreshRequest(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError(error => {
        this.loadingService.hide();
        if (this.isSessionExpiredError(error)) {
          this.handleSessionExpired();
          return throwError(() => new Error('Session expired. Please login again.'));
        }
        return throwError(() => error);
      })
    );
  }

  /** Attach auth header if token exists */
  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const urlToSkip = `${environment.apiBaseUrl}${environment.endpoints.admin_landing_page_images}`;
    if (req.url.startsWith(urlToSkip) && req.method === 'GET') {
      return req;
    }

    const accessToken = localStorage.getItem('access_token');

    if (accessToken) {
      return req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });
    }
    console.error('HTTP Interceptor - No Authorization header added');
    return req;
  }

  private handleLogoUrl(responseBody: any): void {
    if (responseBody && typeof responseBody === 'object' && responseBody.data?.logo_url) {
      localStorage.setItem('logo_url', responseBody.data.logo_url);
    }
  }

  private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.error('HTTP Interceptor - Error:', error);

     // Handle subscription error (error_code 1125)
    const errorCode = error?.error?.error_status?.error_code || error?.error?.error_code;
    if (errorCode === '1125' && !this.isProcessingSubscriptionError) {
      this.processSubscriptionError(error);
    }

    // Check for session expired error in any API response
    if (this.isSessionExpiredError(error)) {
      this.handleSessionExpired();
      return throwError(() => new Error('Session expired. Please login again.'));
    }

    // Check if the error is due to token expiration
    if (this.isTokenExpiredError(error)) {
      return this.handleTokenExpired(error, req, next);
    }

    return throwError(() => error);
  }

  private processSubscriptionError(error: any): void {
    // Clear old subscription plan data to show correct error message
    localStorage.removeItem('subscription_plan');

    // Get error message from the response - try multiple possible locations
    const errorMessage =
      error?.error?.message ||
      error?.error?.error_status?.message ||
      error?.message ||
      'Subscription plan has expired';

    // Immediately show subscription popup
    this.subscriptionPlanService.showSubscriptionPopup(errorMessage);
    this.isProcessingSubscriptionError = true;
    this.fetchSubscriptionStatusAndLogo();
  }

  private fetchSubscriptionStatusAndLogo(): void {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      this.isProcessingSubscriptionError = false; // Reset flag if no access token
      return;
    }

    const url = `${environment.apiBaseUrl}${environment.endpoints.get_corporate_onboarding_status}`;
    this.http.get<any>(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .subscribe(
        async (resp) => {
          if (resp?.data?.subscription_plan_status) {
            const encrypted = await this.encryptionService.encrypt(resp.data.subscription_plan_status);
            localStorage.setItem('subscription_plan', encrypted);
          }
          // Check for and store logo_url if available
          if (resp?.data?.logo_url) {
            localStorage.setItem('logo_url', resp.data.logo_url);
          }
          this.isProcessingSubscriptionError = false; // Reset flag after completion
        },
        () => this.isProcessingSubscriptionError = false // Reset flag on error
      );
  }

  private handleTokenExpired(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add the request to the queue
    this.requestQueue.push(req);

    // If not already refreshing, start the refresh process
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshToken().pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);

          // Retry all queued requests with the new token
          const retryRequests = this.requestQueue.map(request =>
            next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          );

          // Clear the queue
          this.requestQueue = [];

          // Return the first request result
          return retryRequests.length > 0
            ? retryRequests[0]
            : next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.requestQueue = [];
          // Handle refresh token failure (e.g., redirect to login)
          console.error('Token refresh failed:', refreshError);
          return throwError(() => refreshError);
        })
      );
    }

    // If already refreshing, wait for the new token
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token =>
        next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
      )
    );
  }

  private handleSubscriptionErrorResponse(responseBody: any): void {
    // Disable subscription popup in dev
    if (!environment.production) {
      return;
    }
    
    if (!responseBody || typeof responseBody !== 'object') return;

    const errorCode = responseBody.error_status?.error_code;
    const message = responseBody.message;

    if (errorCode === '1125' || (typeof message === 'string' && message.includes('Subscription plan expired'))) {
      // Handle the subscription plan error
      if (!this.isProcessingSubscriptionError) {
        this.processSubscriptionResponseError(responseBody, message);
      }
    }
  }

  private processSubscriptionResponseError(responseBody: any, message: string): void {
    // Clear old subscription plan data to show correct error message
    localStorage.removeItem('subscription_plan');

    // Get error message from the response - try multiple possible locations
    const errorMessage =
      (typeof message === 'string' ? message : '') ||
      responseBody?.message ||
      'Subscription plan has expired';

    // Immediately show subscription popup
    this.subscriptionPlanService.showSubscriptionPopup(errorMessage);
    this.isProcessingSubscriptionError = true;
    this.fetchSubscriptionStatusAndLogo();
  }

  private isTokenExpiredError(error: HttpErrorResponse): boolean {
    // Check for error_code "1121" or "1000" with "Token has expired" or "Token is expired" message
    const errorCode = error?.error?.error_code || error?.error?.error_status?.error_code || error?.error?.message?.error_code;
    const errorMessage = error?.error?.message || error?.error?.error_status?.message || error?.error?.message?.message;
    
    if (errorCode === '1121' || errorCode === '1000') {
      return true;
    }
    
    if (errorMessage === 'Token has expired' || errorMessage === 'Token is expired') {
      return true;
    }
    
    return false;
  }

  private isSessionExpiredError(error: any): boolean {
    // Check both error response and normal response structure
    if (error?.error_status?.error_code === '1122' ||
        error?.message === 'session expired please login again' ||
        (error?.error?.error_status?.error_code === '1122') ||
        (error?.error?.message === 'session expired please login again')) {
      return true;
    }
    return false;
  }

  private handleSessionExpired(): void {
    // Clear tokens first
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Then logout which will handle navigation
    this.authService.logout();
  }

  private refreshToken(): Observable<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      this.loadingService.hide(); // Hide loading screen if no refresh token
      return throwError(() => new Error('No refresh token available'));
    }

    const refreshUrl = `${environment.apiBaseUrl}${environment.endpoints.refresh_token}?refresh_token=${refreshToken}`;

    return from(this.http.post<any>(refreshUrl, {}).toPromise()).pipe(
      switchMap(response => {

        // Check for session expired error in the response
        if (this.isSessionExpiredError(response)) {
          this.loadingService.hide(); // Hide loading screen on session expired
          this.handleSessionExpired();
          return throwError(() => new Error('Session expired. Please login again.'));
        }

        if (response?.data?.access_token) {
          // Store the new access token
          localStorage.setItem('access_token', response.data.access_token);
          return [response.data.access_token];
        }
        this.loadingService.hide(); // Hide loading screen on invalid response structure
        return throwError(() => new Error('Failed to refresh token - Invalid response structure'));
      }),
      catchError(error => {
        this.loadingService.hide(); // Hide loading screen on refresh token error
        // If it's a session expired error from the HTTP error response
        if (this.isSessionExpiredError(error)) {
          this.handleSessionExpired();
          return throwError(() => new Error('Session expired. Please login again.'));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if the request URL is for APIs that should not show loader
   */
  private isNotificationAPI(url: string): boolean {
    // Endpoints that should not show loader
    const noLoaderEndpoints = [
      '/api/v1/notifications/webhook/',
      '/notifications/webhook/',
      '/v1/common/get-locations'
    ];

    return noLoaderEndpoints.some(endpoint => url.includes(endpoint));
  }

  private getLoadingMessage(method: string): string {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'Loading data...';
      case 'POST':
        return 'Please wait...';
      case 'PUT':
        return 'Updating data...';
      case 'DELETE':
        return 'Deleting data...';
      default:
        return 'Please wait...';
    }
  }
}
